import type { FieldValueType } from '@/features/form-factory';
import type { OwnerProduct } from '@/features/operation-packages/api';
import type { ApiResponseBody } from '@/lib/api/api-type-helper';
import { apiClient } from '@/lib/api/client';
import { getDataOrThrowApiError } from '@/lib/api/error';
import { createFormDataSerializer } from '@/lib/api/formDataSerializer';

// --- Instance Detail ---

export type OperationPackageInstanceResponse = ApiResponseBody<'/v2/boe/packages/{ownerProduct}/instances/{instanceId}', 'get'>;
export const getOperationPackageInstance = async (ownerProduct: OwnerProduct, instanceId: string): Promise<OperationPackageInstanceResponse> => {
  const res = await apiClient.GET('/v2/boe/packages/{ownerProduct}/instances/{instanceId}', {
    params: {
      path: {
        ownerProduct,
        instanceId
      }
    }
  });
  return getDataOrThrowApiError(res);
};

// --- Instance Abort ---

export type OperationPackageInstanceArchiveResponse = ApiResponseBody<'/v2/boe/packages/{ownerProduct}/instances/{instanceId}/abort', 'post'>;
export const postOperationPackageInstanceArchive = async (ownerProduct: OwnerProduct, instanceId: string): Promise<OperationPackageInstanceArchiveResponse> => {
  const res = await apiClient.POST('/v2/boe/packages/{ownerProduct}/instances/{instanceId}/abort', {
    params: {
      path: {
        ownerProduct,
        instanceId
      }
    },
    body: undefined
  });
  return getDataOrThrowApiError(res);
};

// --- Instance Progress ---

export type OperationPackageInstanceProgressResponse = ApiResponseBody<'/v2/boe/packages/{ownerProduct}/instances/{instanceId}/progress', 'get'>;
export const getOperationPackageInstanceProgress = async (ownerProduct: OwnerProduct, instanceId: string): Promise<OperationPackageInstanceProgressResponse> => {
  const res = await apiClient.GET('/v2/boe/packages/{ownerProduct}/instances/{instanceId}/progress', {
    params: {
      path: {
        ownerProduct,
        instanceId
      },
      query: {
        type: 'task' as const
      }
    }
  });
  return getDataOrThrowApiError(res);
};

// --- Task Detail ---

export type OperationPackageInstanceTaskResponse = ApiResponseBody<'/v2/boe/packages/{ownerProduct}/instances/{instanceId}/tasks/{taskId}', 'get'>;
export const getOperationPackageInstanceTask = async (ownerProduct: OwnerProduct, instanceId: string, taskId: string): Promise<OperationPackageInstanceTaskResponse> => {
  const res = await apiClient.GET('/v2/boe/packages/{ownerProduct}/instances/{instanceId}/tasks/{taskId}', {
    params: {
      path: {
        ownerProduct,
        instanceId,
        taskId
      }
    }
  });
  return getDataOrThrowApiError(res);
};

// --- Task Complete (Put) ---

export type OperationPackageInstanceTaskCompleteResponse = ApiResponseBody<'/v2/boe/packages/{ownerProduct}/instances/{instanceId}/tasks/{taskId}', 'put'>;

// フォームファクトリーの動的フォームデータの型定義
export type OperationPackageInstanceTaskCompleteBody = Record<string, FieldValueType>;
export const putOperationPackageInstanceTask = async (ownerProduct: OwnerProduct, instanceId: string, taskId: string, body: OperationPackageInstanceTaskCompleteBody): Promise<OperationPackageInstanceTaskCompleteResponse> => {
  const res = await apiClient.PUT('/v2/boe/packages/{ownerProduct}/instances/{instanceId}/tasks/{taskId}', {
    params: {
      path: {
        ownerProduct,
        instanceId,
        taskId
      }
    },
    body,
    bodySerializer: createFormDataSerializer
  });
  return getDataOrThrowApiError(res);
};

// --- Task Approve ---

export type OperationPackageInstanceTaskApproveResponse = ApiResponseBody<'/v2/boe/packages/{ownerProduct}/instances/{instanceId}/tasks/{taskId}/approve', 'post'>;
export const postOperationPackageInstanceTaskApprove = async (ownerProduct: OwnerProduct, instanceId: string, taskId: string): Promise<OperationPackageInstanceTaskApproveResponse> => {
  const res = await apiClient.POST('/v2/boe/packages/{ownerProduct}/instances/{instanceId}/tasks/{taskId}/approve', {
    params: {
      path: {
        ownerProduct,
        instanceId,
        taskId
      }
    },
    body: undefined
  });
  return getDataOrThrowApiError(res);
};

// --- Instance Approve ---

export type OperationPackageInstanceApproveResponse = ApiResponseBody<'/v2/boe/packages/{ownerProduct}/instances/{instanceId}/approve', 'post'>;
export const postOperationPackageInstanceApprove = async (ownerProduct: OwnerProduct, instanceId: string, reqBody: {
  targetTaskIds: string[];
}): Promise<OperationPackageInstanceApproveResponse> => {
  const res = await apiClient.POST('/v2/boe/packages/{ownerProduct}/instances/{instanceId}/approve', {
    params: {
      path: {
        ownerProduct,
        instanceId
      }
    },
    body: {
      taskInstanceIds: reqBody.targetTaskIds
    }
  });
  return getDataOrThrowApiError(res);
};

// --- Instance Data ---

// バックエンドのスキーマでは Record<string, never> と定義されているが、
// 実際には LargeCategoryData[] を返す。
// TODO: バックエンド側でスキーマを修正する必要がある
export type OperationPackageInstanceDataResponse = ApiResponseBody<'/v2/boe/packages/{ownerProduct}/instances/{instanceId}/data', 'get'>;
export const getOperationPackageInstanceData = async (ownerProduct: OwnerProduct, instanceId: string): Promise<OperationPackageInstanceDataResponse> => {
  const res = await apiClient.GET('/v2/boe/packages/{ownerProduct}/instances/{instanceId}/data', {
    params: {
      path: {
        ownerProduct,
        instanceId
      }
    }
  });
  return getDataOrThrowApiError(res);
};